import { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { ToastProvider as ModeToastProvider } from '@stitch-fix/mode-react';
import { PageToast, ToastInfo } from '../../components/PageToast';
interface ToastContext {
  addToast: (toast: ToastInfo) => void;
}
const ToastContext = createContext<ToastContext>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- avoid having consumers unnecessarily check if `addTodo` is defined
  addToast: () => {}
});
export const useToast = () => useContext(ToastContext);
interface ToastInfoState extends ToastInfo {
  id: number;
  isOpen: boolean;
}
const setToastAsClosed = (toasts: ToastInfoState[], toastId: number) => toasts.map(toast => {
  if (toast.id === toastId) {
    return {
      ...toast,
      isOpen: false
    };
  }
  return toast;
});

// Used for setting a "unique" toast id to use as a key when rendering
let count = 0;
const getNextId = () => {
  count += 1;
  return count;
};
interface ToastProviderProps {
  children: ReactNode;
}

/**
 * The `ToastProvider` is used as wrapper around components needing to show a toast on the page.
 *
 * To show a toast in components wrapped in `<ToastProvider>`:
 * - Import and call the `useToast` hook, `const { addToast } = useToast()`.
 * - Then, call `addToast({ message: 'Hello' })`.
 *
 *  Additionally, you can pass an optional `link` and `status` to configure the toast's content.
 */
export const ToastProvider = ({
  children
}: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastInfoState[]>([]);
  const addToast = useCallback((toast: ToastInfo) => {
    setToasts(currentToasts => [...currentToasts, {
      ...toast,
      id: getNextId(),
      isOpen: true
    }]);
  }, []);

  // `toasts` array will only grow since it's difficult to remove them
  // when considering the exit animation timeout set by mode-react.
  // This is okay assuming clients don't trigger a **significant** amount of toasts on the page.
  // Find a better way to remove toasts from the state once they close.
  // https://stitchfix.atlassian.net/browse/CSE-430
  const onClose = useCallback((toastId: number) => setToasts(currentToasts => setToastAsClosed(currentToasts, toastId)), []);
  return <ToastContext.Provider value={{
    addToast
  }}>
      <ModeToastProvider>
        {children}
        {toasts.map(({
        id,
        isOpen,
        message,
        status,
        endContent,
        manualClose,
        delay
      }) => <PageToast key={id} isOpen={isOpen} message={message} status={status} endContent={endContent} onClose={() => onClose(id)} manualClose={manualClose}
      // manualClose has precedence over delay
      delay={manualClose ? undefined : delay} />)}
      </ModeToastProvider>
    </ToastContext.Provider>;
};